// src/pages/league-dashboard/team-home/LastNightBox.js
//
// Extracted from inline definition in TeamHomeDashboard.js per Tier 2
// #9 commit 9d (refactor only — no behavior change).
//
// Largest inline child of any Tier 2 component this session — 790 LoC
// covering: 5 useState hooks (activeTab, selectedPlayerId,
// isModalOpen, isRankingsModalOpen, selectedCategory), 2-tab nav
// (hitting/pitching), per-tab DynastyTable with stat columns,
// rankings tile grid with click-to-open RankingsModal,
// PlayerProfileModal integration.
import React, { useState } from 'react';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { DynastyTable } from '../../../services/tableService';
import { dynastyTheme } from '../../../services/colorService';
import PlayerProfileModal from '../../../components/PlayerProfileModal';
import RankingsModal from '../../../components/BoxScore/RankingsModal';

const LastNightBox = ({ boxData, boxDate, rankings, leagueId, teamId }) => {
  const [activeTab, setActiveTab] = useState('hitting');

  // Player modal state
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Rankings modal state
  const [isRankingsModalOpen, setIsRankingsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('total_hr');

  // Handle player name click
  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };

  // DEBUG: Log rankings data
  console.log('🔍 LastNightBox rankings:', rankings);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Show loading state if boxData is null (still fetching)
  if (boxData === null) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="px-3 pt-1 pb-3">
          <h3 className={dynastyTheme.components.heading.h3}>📊 Box Score</h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
            Loading box scores...
          </div>
        </div>
      </div>
    );
  }

  // Ensure boxData has the correct structure
  const hitters = Array.isArray(boxData?.hitting) ? boxData.hitting : [];
  const pitchers = Array.isArray(boxData?.pitching) ? boxData.pitching : [];

  if (!hitters.length && !pitchers.length) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="px-3 pt-1 pb-3">
          <h3 className={dynastyTheme.components.heading.h3}>📊 Box Score</h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
            <div className="text-5xl mb-3">🌙</div>
            <p className="text-lg">No games played</p>
            <p className={`text-sm mt-2 ${dynastyTheme.classes.text.neutralLighter}`}>
              Your players' performances will appear here after games
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Use pre-calculated team totals from backend BoxScoreService
  const backendHitting = boxData?.team_totals?.hitting || {};
  const backendPitching = boxData?.team_totals?.pitching || {};

  const hittingTotals = {
    isTotals: true,
    ab: backendHitting.at_bats || 0,
    r: backendHitting.runs || 0,
    h: backendHitting.hits || 0,
    doubles: backendHitting.doubles || 0,
    triples: backendHitting.triples || 0,
    hr: backendHitting.home_runs || 0,
    rbi: backendHitting.rbi || 0,
    sb: backendHitting.stolen_bases || 0,
    bb: backendHitting.walks || 0,
    so: backendHitting.strikeouts || 0,
    avg: backendHitting.avg || 0,
    ops: backendHitting.ops || 0
  };

  const pitchingTotals = {
    isTotals: true,
    w: backendPitching.wins || 0,
    l: backendPitching.losses || 0,
    sv: backendPitching.saves || 0,
    ip: backendPitching.innings_pitched || 0,
    h: backendPitching.hits_allowed || 0,
    runs_allowed: backendPitching.runs_allowed || 0,
    er: backendPitching.earned_runs || 0,
    bb: backendPitching.walks_allowed || 0,
    so: backendPitching.strikeouts_pitched || 0,
    home_runs_allowed: backendPitching.home_runs_allowed || 0,
    qs: backendPitching.quality_starts || 0,
    era: backendPitching.era || 0,
    whip: backendPitching.whip || 0
  };

  const formatIP = (ip) => {
    if (ip === null || ip === undefined) return '0.0';
    return ip.toFixed(1);
  };

  // Helper to extract position from lineup_position (e.g., "C_0" -> "C")
  const extractPosition = (lineupPosition) => {
    if (!lineupPosition) return '-';
    return lineupPosition.split('_')[0];
  };

  const hitterColumns = [
    {
      key: 'lineup_position',
      title: 'POS',
      width: 60,
      sortable: true,
      sticky: true,
      stickyLeft: 0,
      render: (value, row) => {
        if (row.isTotals) return <div></div>;
        const pos = extractPosition(row.lineup_position || value || '');
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.warning}`} style={{ fontWeight: '700' }}>
            {pos}
          </div>
        );
      }
    },
    {
      key: 'player_name',
      title: 'Player',
      width: 126,
      sortable: true,
      align: 'left',
      sticky: true,
      stickyLeft: 60,
      render: (value, row) => {
        if (row.isTotals) {
          return (
            <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
              TEAM TOTALS
            </div>
          );
        }
        return (
          <div className="text-left">
            <button
              onClick={() => handlePlayerClick(row.player_id)}
              className={`${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary} ${dynastyTheme.classes.transitionFast} text-left cursor-pointer`}
              style={{ textDecoration: 'none' }}
            >
              {value || '-'}
            </button>
          </div>
        );
      }
    },
    {
      key: 'mlb_team',
      title: 'Team',
      width: 80,
      sortable: true,
      render: (value, row) => {
        if (row.isTotals) return null;
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
            {value || '-'}
          </div>
        );
      }
    },
    {
      key: 'at_bats',
      title: 'AB',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.ab : (row.stats?.hitting?.at_bats || 0)}
        </div>
      )
    },
    {
      key: 'runs',
      title: 'R',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.r : (row.stats?.hitting?.runs || 0)}
        </div>
      )
    },
    {
      key: 'hits',
      title: 'H',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.h : (row.stats?.hitting?.hits || 0)}
        </div>
      )
    },
    {
      key: 'doubles',
      title: '2B',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (row.doubles || 0) : (row.stats?.hitting?.doubles || 0)}
        </div>
      )
    },
    {
      key: 'triples',
      title: '3B',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (row.triples || 0) : (row.stats?.hitting?.triples || 0)}
        </div>
      )
    },
    {
      key: 'home_runs',
      title: 'HR',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
          {row.isTotals ? row.hr : (row.stats?.hitting?.home_runs || 0)}
        </div>
      )
    },
    {
      key: 'rbi',
      title: 'RBI',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.rbi : (row.stats?.hitting?.rbi || 0)}
        </div>
      )
    },
    {
      key: 'walks',
      title: 'BB',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.bb : (row.stats?.hitting?.walks || 0)}
        </div>
      )
    },
    {
      key: 'stolen_bases',
      title: 'SB',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.sb : (row.stats?.hitting?.stolen_bases || 0)}
        </div>
      )
    },
    {
      key: 'strikeouts',
      title: 'K',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.so : (row.stats?.hitting?.strikeouts || 0)}
        </div>
      )
    },
    {
      key: 'batting_avg',
      title: 'AVG',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? formatBattingAvg(row.avg) : formatBattingAvg(row.stats?.hitting?.batting_avg)}
        </div>
      )
    },
    {
      key: 'ops',
      title: 'OPS',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? formatBattingAvg(row.ops) : formatBattingAvg(row.stats?.hitting?.ops)}
        </div>
      )
    }
  ];

  const pitcherColumns = [
    {
      key: 'player_name',
      title: 'Player',
      width: 180,
      sortable: true,
      align: 'left',
      sticky: true,
      stickyLeft: 0,
      render: (value, row) => {
        if (row.isTotals) {
          return (
            <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
              TEAM TOTALS
            </div>
          );
        }
        return (
          <div className="text-left">
            <button
              onClick={() => handlePlayerClick(row.player_id)}
              className={`${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary} ${dynastyTheme.classes.transitionFast} text-left cursor-pointer`}
              style={{ textDecoration: 'none' }}
            >
              {value || '-'}
            </button>
            {row.stats?.pitching?.games_started > 0 && (
              <div className={`text-xs ${dynastyTheme.classes.text.primary}`}>
                Starter
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'mlb_team',
      title: 'Team',
      width: 80,
      sortable: true,
      render: (value, row) => {
        if (row.isTotals) return null;
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
            {value || '-'}
          </div>
        );
      }
    },
    {
      key: 'games_pitched',
      title: 'G',
      width: 50,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (backendPitching.games_pitched || 0) : (row.stats?.pitching?.games_pitched || 0)}
        </div>
      )
    },
    {
      key: 'games_started',
      title: 'GS',
      width: 50,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (backendPitching.games_started || 0) : (row.stats?.pitching?.games_started || 0)}
        </div>
      )
    },
    {
      key: 'innings_pitched',
      title: 'IP',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? formatIP(row.ip) : formatIP(row.stats?.pitching?.innings_pitched)}
        </div>
      )
    },
    {
      key: 'hits_allowed',
      title: 'H',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.h : (row.stats?.pitching?.hits_allowed || 0)}
        </div>
      )
    },
    {
      key: 'runs_allowed',
      title: 'R',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (row.runs_allowed || 0) : (row.stats?.pitching?.runs_allowed || 0)}
        </div>
      )
    },
    {
      key: 'earned_runs',
      title: 'ER',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.er : (row.stats?.pitching?.earned_runs || 0)}
        </div>
      )
    },
    {
      key: 'walks',
      title: 'BB',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.bb : (row.stats?.pitching?.walks_allowed || 0)}
        </div>
      )
    },
    {
      key: 'strikeouts_pitched',
      title: 'K',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
          {row.isTotals ? row.so : (row.stats?.pitching?.strikeouts_pitched || 0)}
        </div>
      )
    },
    {
      key: 'home_runs_allowed',
      title: 'HR',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? (row.home_runs_allowed || 0) : (row.stats?.pitching?.home_runs_allowed || 0)}
        </div>
      )
    },
    {
      key: 'wins',
      title: 'W',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
          {row.isTotals ? row.w : (row.stats?.pitching?.wins || 0)}
        </div>
      )
    },
    {
      key: 'saves',
      title: 'SV',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
          {row.isTotals ? row.sv : (row.stats?.pitching?.saves || 0)}
        </div>
      )
    },
    {
      key: 'quality_starts',
      title: 'QS',
      width: 60,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? row.qs : (row.stats?.pitching?.quality_starts || 0)}
        </div>
      )
    },
    {
      key: 'era',
      title: 'ERA',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.white}>
          {row.isTotals ? formatERA(row.era) : formatERA(row.stats?.pitching?.era)}
        </div>
      )
    },
    {
      key: 'whip',
      title: 'WHIP',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <div className={row.isTotals ? `font-bold ${dynastyTheme.classes.text.primary}` : dynastyTheme.classes.text.neutralLight}>
          {row.isTotals ? formatBattingAvg(row.whip) : formatBattingAvg(row.stats?.pitching?.whip)}
        </div>
      )
    }
  ];

  // Append totals to data arrays with isTotals flag
  const hitterData = [...hitters, { ...hittingTotals, isTotals: true }];
  const pitcherData = [...pitchers, { ...pitchingTotals, isTotals: true }];

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className="px-3 pt-1 pb-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className={`${dynastyTheme.components.heading.h3} !mb-0`}>📊 Box Score</h3>
          {boxDate && (
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              {formatDate(boxDate)}
            </div>
          )}
        </div>

        {/* Summary Stats with Rankings */}
        <div className="mb-6">
          {/* Helper functions for rankings */}
          {(() => {
            const getRankSuffix = (num) => {
              if (num % 10 === 1 && num !== 11) return 'st';
              if (num % 10 === 2 && num !== 12) return 'nd';
              if (num % 10 === 3 && num !== 13) return 'rd';
              return 'th';
            };

            const getRankStyling = (rank) => {
              if (!rank) return { containerClass: '', badgeClass: '', showBadge: false };

              let containerClass = 'relative ';
              let badgeClass = '';
              let emoji = '';

              if (rank.is_first) {
                containerClass += 'border-3 border-yellow-500 shadow-[0_0_20px_rgba(255,215,0,0.6)] animate-[pulse_2s_ease-in-out_infinite]';
                badgeClass = 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black';
                emoji = '🔥';
              } else if (rank.rank === 2) {
                containerClass += 'border-2 border-gray-300 shadow-[0_0_15px_rgba(192,192,192,0.4)]';
                badgeClass = 'bg-gradient-to-br from-gray-300 to-gray-400 text-black';
              } else if (rank.rank === 3) {
                containerClass += 'border-2 border-amber-600 shadow-[0_0_15px_rgba(205,127,50,0.4)]';
                badgeClass = 'bg-gradient-to-br from-amber-600 to-amber-700 text-white';
              } else if (rank.is_last) {
                containerClass += 'border-2 border-blue-400 shadow-[0_0_15px_rgba(74,144,226,0.3)] opacity-85';
                badgeClass = 'bg-gradient-to-br from-blue-400 to-blue-600 text-white';
                emoji = '❄️';
              } else {
                containerClass += 'border border-neutral-700';
                badgeClass = 'bg-neutral-700 text-neutral-400';
              }

              const rankText = `${rank.rank}${getRankSuffix(rank.rank)} of ${rank.total_teams}`;
              return { containerClass, badgeClass, rankText, emoji, showBadge: true };
            };

            // Chunk 3.14 — format a stat value per its rankKey vocabulary.
            // Counting stats (total_*) → integer; AVG/OPS/WHIP → 3 decimals;
            // ERA → 2 decimals. null/undefined → "—" (neutral state).
            const formatStat = (rankKey, v) => {
              if (v === null || v === undefined) return '—';
              if (rankKey === 'era') return formatERA(v);
              if (rankKey === 'avg' || rankKey === 'ops' || rankKey === 'whip') return formatBattingAvg(v);
              return v;
            };
            const formatDelta = (rankKey, delta) => {
              if (delta === null || delta === undefined) return '—';
              const isRate = ['avg', 'ops', 'era', 'whip'].includes(rankKey);
              if (isRate) {
                // Rate deltas: signed 3-decimal (e.g. +.016, -1.00 for ERA).
                if (rankKey === 'era') return `${delta >= 0 ? '+' : ''}${Number(delta).toFixed(2)}`;
                return `${delta >= 0 ? '+' : ''}${Number(delta).toFixed(3).replace(/^0\./, '.').replace(/^-0\./, '-.')}`;
              }
              return `${delta >= 0 ? '+' : ''}${delta}`;
            };

            // Chunk 3.14 — sparkline SVG (6 weekly points). Renders polyline
            // over the data range; null points → gaps (segmented polylines).
            // Color follows trend direction (up/down/flat/none).
            const renderSparkline = (history, direction, rankKey) => {
              if (!history || history.length === 0) return null;
              const pts = history.slice(0, 6);
              const numeric = pts.filter(v => v !== null && v !== undefined).map(Number);
              if (numeric.length === 0) {
                // All-None: render flat neutral line
                return (
                  <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '64px', height: '22px' }}>
                    <line x1="0" y1="14" x2="100" y2="14" stroke="#3f3f46" strokeWidth="1" strokeDasharray="3,3" />
                  </svg>
                );
              }
              const minV = Math.min(...numeric);
              const maxV = Math.max(...numeric);
              const range = maxV - minV || 1;
              const xs = [0, 20, 40, 60, 80, 100];
              const ys = pts.map(v => {
                if (v === null || v === undefined) return null;
                return 26 - ((v - minV) / range) * 22;
              });
              const strokeColor =
                direction === 'up'   ? '#34d399' :
                direction === 'down' ? '#f87171' :
                direction === 'flat' ? '#a1a1aa' :
                '#52525b';
              const gradId = direction === 'up' ? 'grad-up' : direction === 'down' ? 'grad-down' : 'grad-flat';
              // Build segments — contiguous runs of non-null points.
              const segments = [];
              let current = [];
              for (let i = 0; i < 6; i++) {
                if (ys[i] === null) {
                  if (current.length >= 2) segments.push(current);
                  current = [];
                } else {
                  current.push(`${xs[i]},${ys[i]}`);
                }
              }
              if (current.length >= 2) segments.push(current);
              const lastIdx = ys.map((y, i) => y !== null ? i : -1).filter(i => i >= 0).pop();
              const lastX = lastIdx != null ? xs[lastIdx] : null;
              const lastY = lastIdx != null ? ys[lastIdx] : null;
              return (
                <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '64px', height: '22px' }}>
                  <defs>
                    <linearGradient id={`spark-${gradId}-${rankKey}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={strokeColor} stopOpacity="0.35" />
                      <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {segments.map((seg, i) => {
                    const fillPath = `M${seg[0]} L${seg.slice(1).map(p => p).join(' L ')} L${seg[seg.length-1].split(',')[0]},28 L${seg[0].split(',')[0]},28 Z`;
                    return (
                      <g key={i}>
                        {seg.length >= 2 && (
                          <path d={fillPath.replace(/L([\d.]+),([\d.]+)/g, 'L $1,$2').replace(/M([\d.]+),([\d.]+)/, 'M $1,$2')} fill={`url(#spark-${gradId}-${rankKey})`} />
                        )}
                        <polyline points={seg.join(' ')} fill="none" stroke={strokeColor} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                      </g>
                    );
                  })}
                  {lastX !== null && lastY !== null && (
                    <>
                      <circle cx={lastX} cy={lastY} r="3" fill={strokeColor} opacity="0.3" />
                      <circle cx={lastX} cy={lastY} r="1.7" fill={strokeColor} />
                    </>
                  )}
                </svg>
              );
            };

            const renderTrendIcon = (direction) => {
              if (direction === 'up') return (
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M6 2 L10 8 L2 8 Z" fill="currentColor"/></svg>
              );
              if (direction === 'down') return (
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M6 10 L10 4 L2 4 Z" fill="currentColor"/></svg>
              );
              if (direction === 'flat') return (
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              );
              return (
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeDasharray="2,2"/></svg>
              );
            };

            const trendColor = (direction) => {
              if (direction === 'up') return 'text-emerald-400';
              if (direction === 'down') return 'text-red-400';
              if (direction === 'flat') return 'text-zinc-400';
              return 'text-zinc-600';
            };

            const renderStatTile = (label, value, rankKey) => {
              const rank = rankings?.your_ranks?.[rankKey];
              console.log(`🎯 Tile "${label}" - rankKey: ${rankKey}, rank:`, rank);
              const { containerClass, badgeClass, rankText, emoji, showBadge } = getRankStyling(rank);

              // Check if we have allRankings data to make tile clickable
              const allRankings = rankings?.all_rankings || null;
              const hasRankingsData = !!allRankings;

              // Chunk 3.14 — new fields from backend enrichment.
              const weekVal = rankings?.your_team_weekly?.[rankKey];
              const seasonVal = rankings?.your_team_season?.[rankKey];
              const wow = rankings?.your_team_wow?.[rankKey];
              const history = rankings?.your_team_weekly_history?.[rankKey];
              const direction = wow?.direction || 'none';
              const delta = wow?.delta;

              return (
                <div
                  onClick={() => {
                    if (hasRankingsData) {
                      setSelectedCategory(rankKey);
                      setIsRankingsModalOpen(true);
                    }
                  }}
                  className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${containerClass} ${
                    hasRankingsData ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                  }`}
                >
                  {showBadge && (
                    <div className={`absolute top-2 right-2 ${badgeClass} px-2 py-1 rounded text-[10px] font-bold z-10`}>
                      {rankText}
                    </div>
                  )}
                  {emoji && (
                    <div className="absolute bottom-1 right-1 text-lg z-10">
                      {emoji}
                    </div>
                  )}
                  <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                    {label}
                  </div>
                  <div className={`text-xl font-bold ${dynastyTheme.classes.text.primary}`}>
                    {value}
                  </div>

                  {/* Chunk 3.14 — subline: Wk/Szn + sparkline + delta */}
                  {(weekVal !== undefined || seasonVal !== undefined || history) && (
                    <>
                      <div className="flex items-center gap-3 mt-2 pt-2" style={{ borderTop: '1px solid rgba(82,82,91,0.25)' }}>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-600">Wk</div>
                          <div className="text-[11px] font-semibold text-zinc-300 tabular-nums">{formatStat(rankKey, weekVal)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-600">Szn</div>
                          <div className="text-[11px] font-semibold text-zinc-300 tabular-nums">{formatStat(rankKey, seasonVal)}</div>
                        </div>
                      </div>
                      <div className="flex items-end justify-between mt-1.5">
                        {renderSparkline(history, direction, rankKey)}
                        <div className={`flex items-center gap-1 ${trendColor(direction)}`}>
                          {renderTrendIcon(direction)}
                          <span className="text-[11px] font-semibold tabular-nums">{formatDelta(rankKey, delta)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            };

            return (
              <>
                {/* Hitting Stats */}
                <div className="mb-3">
                  <div className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                    ⚾ Hitting
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {renderStatTile('Runs', hittingTotals.r || 0, 'total_runs')}
                    {renderStatTile('Home Runs', hittingTotals.hr || 0, 'total_hr')}
                    {renderStatTile('RBI', hittingTotals.rbi || 0, 'total_rbi')}
                    {renderStatTile('Stolen Bases', hittingTotals.sb || 0, 'total_sb')}
                    {renderStatTile('AVG', formatBattingAvg(hittingTotals.avg), 'avg')}
                    {renderStatTile('OPS', formatBattingAvg(hittingTotals.ops), 'ops')}
                  </div>
                </div>

                {/* Pitching Stats */}
                <div>
                  <div className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                    🥎 Pitching
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {renderStatTile('Wins', pitchingTotals.w || 0, 'total_wins')}
                    {renderStatTile('Saves', pitchingTotals.sv || 0, 'total_saves')}
                    {renderStatTile('ERA', formatERA(pitchingTotals.era), 'era')}
                    {renderStatTile('WHIP', formatBattingAvg(pitchingTotals.whip), 'whip')}
                    {renderStatTile('Strikeouts', pitchingTotals.so || 0, 'total_k')}
                    {renderStatTile('Quality Starts', pitchingTotals.qs || 0, 'total_qs')}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Tab Headers */}
        <div className={`flex border-b ${dynastyTheme.classes.border.neutral} mb-4`}>
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'hitting'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                : dynastyTheme.classes.text.neutralLight
            }`}
          >
            Hitters ({boxData.hitting?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ml-6 ${
              activeTab === 'pitching'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                : dynastyTheme.classes.text.neutralLight
            }`}
          >
            Pitchers ({boxData.pitching?.length || 0})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'hitting' ? (
          <div
            className="box-score-hitting-wrapper"
            style={{
              overflowX: 'auto',
              isolation: 'isolate',
              position: 'relative'
            }}
          >
            <style>{`
              /* Sticky POS column */
              .box-score-hitting-wrapper table thead th:first-child,
              .box-score-hitting-wrapper table tbody td:first-child {
                position: sticky !important;
                left: 0 !important;
                z-index: 11 !important;
                background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
                border-right: none !important;
              }
              .box-score-hitting-wrapper table tbody tr td:first-child {
                background-color: inherit !important;
              }

              /* Sticky Player column */
              .box-score-hitting-wrapper table thead th:nth-child(2),
              .box-score-hitting-wrapper table tbody td:nth-child(2) {
                position: sticky !important;
                left: 60px !important;
                z-index: 10 !important;
                background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
                box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3) !important;
              }
              .box-score-hitting-wrapper table tbody tr td:nth-child(2) {
                background-color: inherit !important;
              }

              /* Team totals row - yellow border separator */
              .box-score-hitting-wrapper table tbody tr:last-child {
                border-top: 2px solid ${dynastyTheme.tokens.colors.primary} !important;
              }
              .box-score-hitting-wrapper table tbody tr:last-child td {
                background-color: ${dynastyTheme.tokens.colors.neutral[750]} !important;
              }
            `}</style>
            <DynastyTable
              data={hitterData}
              columns={hitterColumns}
              tableType="flat-data"
              maxHeight="600px"
              initialSort={{ key: 'home_runs', direction: 'desc' }}
            />
          </div>
        ) : (
          <div
            className="box-score-pitching-wrapper"
            style={{
              overflowX: 'auto',
              isolation: 'isolate',
              position: 'relative'
            }}
          >
            <style>{`
              /* Sticky Player column */
              .box-score-pitching-wrapper table thead th:first-child,
              .box-score-pitching-wrapper table tbody td:first-child {
                position: sticky !important;
                left: 0 !important;
                z-index: 10 !important;
                background-color: ${dynastyTheme.tokens.colors.neutral[800]} !important;
                box-shadow: 2px 0 4px rgba(0, 0, 0, 0.3) !important;
              }
              .box-score-pitching-wrapper table tbody tr td:first-child {
                background-color: inherit !important;
              }

              /* Team totals row - yellow border separator */
              .box-score-pitching-wrapper table tbody tr:last-child {
                border-top: 2px solid ${dynastyTheme.tokens.colors.primary} !important;
              }
              .box-score-pitching-wrapper table tbody tr:last-child td {
                background-color: ${dynastyTheme.tokens.colors.neutral[750]} !important;
              }
            `}</style>
            <DynastyTable
              data={pitcherData}
              columns={pitcherColumns}
              tableType="flat-data"
              maxHeight="600px"
              initialSort={{ key: 'innings_pitched', direction: 'desc' }}
            />
          </div>
        )}
      </div>

      {/* Player Profile Modal */}
      <PlayerProfileModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userTeamId={teamId}
      />

      {/* Rankings Modal */}
      <RankingsModal
        isOpen={isRankingsModalOpen}
        onClose={() => setIsRankingsModalOpen(false)}
        allRankings={rankings?.all_rankings || null}
        yourTeamId={teamId}
        defaultCategory={selectedCategory}
        statsDate={rankings?.stats_date || boxDate || ''}
      />
    </div>
  );
};

export default LastNightBox;
